import { UserRewardsQueryKeys } from '@/api/user-rewards/queryKeys';
import { RewardId } from '@/enums/reward-id';
import { DetailedUserReward } from '@/models/user-reward/detailed';
import { UserRewardStatusId } from '@/models/user-reward/status/id';
import { platformAssets } from '@/utils/assets';
import { useCountdown } from '@starsoft/common/hooks';
import { WalletService } from '@starsoft/common/services';
import { getMoneyOrAmount } from '@starsoft/common/utils';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { UseBonusRewardCardStateProps } from './props';

export default function useBonusRewardCardState({
  rewardId,
  userReward,
}: UseBonusRewardCardStateProps) {
  const { t } = useTranslation('common');
  const queryClient: QueryClient = useQueryClient();
  const dynamicReward: DetailedUserReward = userReward;

  const formattedValue = WalletService.maskCurrency({
    amount: getMoneyOrAmount(dynamicReward?.amount),
    coin: dynamicReward?.coin,
  });

  const isLevelUpReward: boolean =
    dynamicReward?.reward?.id?.toString() === RewardId.LevelUp.toString();

  const isDisabled: boolean = !userReward || !userReward?.vesting;

  const countdown = useCountdown({
    targetDate:
      userReward?.reward?.statusId?.toString() ===
      UserRewardStatusId.InProgress.toString()
        ? new Date(userReward?.vesting?.expiresAt ?? new Date())?.toUTCString()
        : new Date(
            userReward?.vesting?.availableAt ?? new Date(),
          )?.toUTCString(),
    disabled: isDisabled,
    onCountdownComplete: () => {
      if (isDisabled) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.Me],
      });
      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.ById],
      });
      queryClient.invalidateQueries({
        queryKey: [UserRewardsQueryKeys.Trackers],
      });
    },
  });

  const { isCounting } = countdown;

  const icon = `${process.env.NEXT_PUBLIC_S3_BUCKET_BASE_URL}/icons/bonus/${userReward?.reward?.image}`;
  const title = isLevelUpReward
    ? t('level_up_rank_title', {
        rank_group: platformAssets.app,
      })
    : t('earnback_title', {
        reward: t(`bonus:${dynamicReward?.reward?.id}_reward_name`),
        date: Intl.DateTimeFormat('pt-BR', {
          day: '2-digit',
          month: '2-digit',
        }).format(new Date(userReward?.vesting?.createdAt ?? new Date())),
      });

  const description = isLevelUpReward
    ? t('level_up_rank_description')
    : t('earnback_description', {
        app_provider: platformAssets.app,
        reward: t(`bonus:${dynamicReward?.reward?.id}_reward_name`),
        coin: formattedValue,
        time: isCounting ? '' : t('now'),
      });

  const isNegativeRewardAmount = dynamicReward?.amount?.isNegative?.();

  return {
    description,
    dynamicReward,
    countdown,
    title,
    icon,
    isLevelUpReward,
    formattedValue,
    isNegativeRewardAmount,
    coin: dynamicReward?.coin,
  };
}
